import React, { ChangeEvent, FocusEvent, KeyboardEvent, MouseEvent, ReactNode, useState } from 'react';
import {
  Box,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  OutlinedInput,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { Eye, EyeOff } from 'lucide-react';

export interface TextInputProps {
  label?: string;
  name: string;
  value?: string | number;
  type?: string;
  placeholder?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  onClick?: (event: MouseEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  min?: string | number;
  max?: string | number;
  step?: string | number;
  required?: boolean;
  className?: string;
  inputClassName?: string;
  variant?: 'default' | 'pill';
  error?: boolean;
  helperText?: ReactNode;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  autoFocus?: boolean;
  autoComplete?: string;
  fullWidth?: boolean;
  id?: string;
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

export const TextInput = ({
  label,
  name,
  value = '',
  type = 'text',
  placeholder = '',
  onChange,
  disabled = false,
  onClick,
  onBlur,
  onFocus,
  onKeyDown,
  min,
  max,
  step,
  required = false,
  className,
  inputClassName,
  variant = 'default',
  error = false,
  helperText,
  startAdornment,
  endAdornment,
  autoFocus = false,
  autoComplete,
  fullWidth = true,
  id,
  size = 'small',
  sx,
  inputProps,
}: TextInputProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === 'password';
  const resolvedType = isPassword ? (showPassword ? 'text' : 'password') : type;
  const isPill = variant === 'pill';
  const inputId = id || name;

  return (
    <FormControl
      fullWidth={fullWidth}
      error={Boolean(error)}
      disabled={disabled}
      className={className}
      sx={{
        width: fullWidth ? '100%' : 'auto',
        ...sx,
      }}
    >
      {label && (
        <Typography
          component="label"
          htmlFor={inputId}
          sx={{
            display: 'block',
            mb: 0.75,
            fontSize: '0.75rem',
            fontWeight: 600,
            color: error ? 'error.main' : 'text.primary',
            letterSpacing: '0.01em',
            fontFamily: 'inherit',
          }}
        >
          {label}
          {required && (
            <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
              *
            </Box>
          )}
        </Typography>
      )}

      <OutlinedInput
        id={inputId}
        name={name}
        type={resolvedType}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        onClick={onClick}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        disabled={disabled}
        required={required}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        size={size}
        startAdornment={
          startAdornment ? (
            <InputAdornment position="start">{startAdornment}</InputAdornment>
          ) : undefined
        }
        endAdornment={
          isPassword || endAdornment ? (
            <InputAdornment position="end">
              {isPassword && (
                <IconButton
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                  size="small"
                  tabIndex={-1}
                  sx={{
                    color: 'text.secondary',
                    p: 0.5,
                    mr: endAdornment ? 0.5 : -0.25,
                    transition: 'color 150ms ease',
                    '&:hover': { color: 'text.primary' },
                  }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </IconButton>
              )}
              {endAdornment}
            </InputAdornment>
          ) : undefined
        }
        inputProps={{
          min,
          max,
          step,
          className: inputClassName,
          ...inputProps,
        }}
        sx={{
          borderRadius: isPill ? '9999px' : '12px',
          fontFamily: 'inherit',
          color: '#0f172a',
          transition: 'all 200ms ease',
          backgroundColor: isPill ? '#f4f4f5' : '#ffffff',
          '& .MuiOutlinedInput-input': {
            color: '#0f172a !important',
            WebkitTextFillColor: '#0f172a !important',
            outline: 'none !important',
            boxShadow: 'none !important',
            border: 'none !important',
            caretColor: '#0f172a',
            fontSize: '0.875rem',
            fontWeight: 500,
            padding: isPill ? '10px 18px' : '10px 14px',
            '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
              WebkitBoxShadow: '0 0 0 1000px #ffffff inset !important',
              WebkitTextFillColor: '#0f172a !important',
              caretColor: '#0f172a !important',
              transition: 'background-color 5000s ease-in-out 0s',
            },
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#e4e4e7',
            transition: 'border-color 200ms ease, box-shadow 200ms ease',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#cbd5e1',
          },
          '&.Mui-focused': {
            backgroundColor: '#ffffff',
            boxShadow: '0 0 0 3px rgba(5, 150, 105, 0.15)',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#059669 !important',
              borderWidth: '1.5px !important',
            },
          },
          ...(disabled && {
            backgroundColor: '#f1f5f9',
            cursor: 'not-allowed',
          }),
        }}
      />

      {helperText && (
        <FormHelperText
          sx={{
            ml: 0.5,
            mt: 0.5,
            fontSize: '0.72rem',
            fontFamily: 'inherit',
            color: error ? 'error.main' : 'text.secondary',
          }}
        >
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};
